import { useMeasureReadings, usePatientRecord } from '@/state/data/patient';
import { measureWindowYmd, OVERVIEW_SERIES_DAYS } from '@/logic/measureWindow';
import { pairBpReadings } from '@/logic/bpReadings';
import { buildWeightSeries, WEIGHT_MEASTYPES, WEIGHT_TYPES } from '@/logic/weightReadings';
import { formatReportDate } from '@/logic/cmsWindows';
import {
  evaluateOapRows, oapCounts, type CarePlanLike, type MeasureStateLike,
} from '@/logic/oapAttainment';
import type { BaselineFilingRow } from '@/logic/baseline';
import useNow from '@/hooks/useNow';
import GoalRow from './GoalRow';

// The CMS lens on the patient's numbers. Renders nothing before alignment + an accepted baseline
// filing (D2). Its two series share the readings cards' query keys, so TanStack fetches once.

interface GoalScoreboardProps {
  withingsUserId: string | number;
  carePeriod?: { cms_alignment_date?: string | null } | null;
  carePlan?: CarePlanLike | null;
  measureStates?: readonly MeasureStateLike[] | null;
  cmsSubmissions?: readonly BaselineFilingRow[] | null;
  trackCode?: string | null;
  subtrackId?: string | null;
  now?: number;
}

const HEAD = ['Measure', 'Baseline', 'Current', `${OVERVIEW_SERIES_DAYS} days`, 'CMS goal', 'Gap', 'CMS status'];

export default function GoalScoreboard({
  withingsUserId, carePeriod = null, carePlan = null, measureStates = null, cmsSubmissions = null,
  trackCode = null, subtrackId = null, now,
}: GoalScoreboardProps) {
  const ticking = useNow();
  const at = now ?? ticking;
  const aligned = !!carePeriod?.cms_alignment_date;
  const { startdateymd, enddateymd } = measureWindowYmd(OVERVIEW_SERIES_DAYS, at);
  // Same keys as BpReadingsCard / WeightReadingsCard on their default range.
  const bpQuery = useMeasureReadings(aligned ? withingsUserId : null, '9,10', { startdateymd, enddateymd });
  const wtQuery = useMeasureReadings(aligned ? withingsUserId : null, WEIGHT_MEASTYPES, { startdateymd, enddateymd });
  // The shell already loaded the record, so this is a cache hit; its zone decides what "one day" is for BP.
  const record = usePatientRecord(aligned ? withingsUserId : null);

  if (!aligned) return null;
  const bpReadings = bpQuery.data ? pairBpReadings(bpQuery.data.readings) : null;
  const weightReadings = wtQuery.data ? (buildWeightSeries(wtQuery.data.readings)[WEIGHT_TYPES.weight] ?? []) : null;
  const timeZone = record.data?.timezone ?? null;
  const ev = evaluateOapRows({
    trackCode, subtrackId, carePlan, measureStates, cmsSubmissions, withingsUserId, bpReadings, weightReadings, timeZone, now: at,
  });
  if (!ev) return null;

  const loading = bpQuery.isLoading || wtQuery.isLoading;
  // A failed fetch must not read as a clinical verdict: the device rows say so instead.
  const seriesError = bpQuery.isError || wtQuery.isError;
  const counts = oapCounts(ev.rows);
  let countCopy = 'no scored target';
  if (loading || seriesError) countCopy = seriesError ? 'readings unavailable' : 'loading readings…';
  else if (counts.required > 0) countCopy = `${counts.met} of ${counts.required} met`;
  return (
    <section className="oap-board" aria-label="CMS goals">
      <div className="oap-board__head">
        <span className="eyebrow text-secondary">CMS goals</span>
        <span className="detail1 text-secondary">
          {countCopy}
          {ev.filedAt ? ` · baseline filed ${formatReportDate(Date.parse(ev.filedAt))}` : ''}
        </span>
      </div>
      <div className="oap-grid" role="table" aria-label="CMS goal attainment by measure">
        <div className="oap-row oap-row--head" role="row">
          {HEAD.map((h) => <div key={h} className="oap-cell detail1 text-secondary" role="columnheader">{h}</div>)}
        </div>
        {ev.rows.map((row) => <GoalRow key={row.key} row={row} loading={loading} seriesError={seriesError} />)}
      </div>
    </section>
  );
}
